/* Marcel Afters · POP layer script (4 Oct 2026). Shared by the storefront and the wholesale portal. No library, about 6 KB.
   What it does, in order of importance to the shopper:
     1. press     instant response on pointer-down (Web Animations on `scale`), spring release, interruptible
     2. images    below-the-fold pictures get a tint + sweep while they load, then settle in (never the LCP image)
     3. reveal    blocks marked data-pop-reveal rise once, staggered, when they scroll in (IntersectionObserver, unobserved after)
     4. rails     horizontal rails get an edge-aware fade hint and a progress line on phones
     5. sheet     MaPop.sheet(): a bottom sheet you can grab, with 1:1 tracking, velocity projection and a spring settle
   Everything degrades to the plain page: no JS, reduced motion, or an old browser. Reduced motion keeps the instant
   feedback (colour) and drops movement. Safe to load twice (the second load does nothing). */
(function () {
  'use strict';
  if (window.MaPop) return;
  var root = document.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  function calm() { return !!(reduce && reduce.matches); }
  var SPRING = 'cubic-bezier(0.22, 1, 0.36, 1)';
  try { if (window.CSS && CSS.supports('transition-timing-function', 'linear(0, 1)')) SPRING = getComputedStyle(root).getPropertyValue('--pop-spring').trim() || SPRING; } catch (e) { /* keep the bezier */ }
  var canScale = !!(window.CSS && CSS.supports && CSS.supports('scale', '1'));
  root.classList.add('pop-js');
  /* iOS only paints :active once something listens for touch. */
  document.addEventListener('touchstart', function () {}, { passive: true });

  /* ---------- 1. press ---------- */
  var PRESS = [
    '.ma-btn', '.button', '.button-secondary', '.add-to-cart-button', '.ma-hero-chips a', '.ma-st__tab', '.ma-gx-tabs__b', '.ma-x__btn', '.ma-rc__btn',
    '.ma-qs__plus', '.ma-value-strip a', '.header-actions__action', '.maws-btn', '.maws-chip', '.maws-tab', '.maws-seg button', '.maws-iconbtn',
    '.maws-cap', '.maws-pill__order', '.maws-pill__btn', '.maws-pill__links a', '.maws-x', '.maws-sw button', '.maws-radio', '.maws-check-line'
  ].join(',');
  var CARDS = 'product-card, .ma-rc__item > a, .maws-pcard__open, .maws-card, .maws-cap, .maws-vert, .maws-tier';
  var held = null;
  function release(el, anim) {
    if (!el || !canScale) return;
    var from = getComputedStyle(el).scale; /* the live on-screen value: an interrupted press starts from here */
    if (anim) anim.cancel();
    if (calm()) return;
    var back = el.animate({ scale: [from === 'none' ? '0.97' : from, '1'] }, { duration: 460, easing: SPRING });
    back.onfinish = back.oncancel = function () { try { back.cancel(); } catch (e) { /* done */ } };
    el.__popBack = back;
  }
  document.addEventListener('pointerdown', function (e) {
    if (e.button > 0 || !canScale || calm()) return;
    var t = e.target.closest && e.target.closest(PRESS + ',' + CARDS);
    if (!t || t.disabled || t.getAttribute('aria-disabled') === 'true') return;
    if (t.__popBack) { try { t.__popBack.cancel(); } catch (x) { /* gone */ } }
    var soft = !t.matches(PRESS);
    var a = t.animate({ scale: ['1', soft ? '0.985' : '0.965'] }, { duration: 110, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'forwards' });
    held = { el: t, anim: a };
    function up() {
      document.removeEventListener('pointerup', up, true); document.removeEventListener('pointercancel', up, true);
      if (held && held.el === t) { release(t, held.anim); held = null; }
    }
    document.addEventListener('pointerup', up, true); document.addEventListener('pointercancel', up, true);
  }, { passive: true });

  /* ---------- 2. images ---------- */
  /* One IntersectionObserver with a long runway watches lazy pictures. When one comes within 3000px (before the browser starts to load it)
     and is still below the first screen, it gets the tint + sweep, then settles in on load. The rect comes from the observer entry, so there
     are no layout reads and no layout thrash, however many pictures the page has. */
  // Product cards own model/flat visibility; loader opacity must not reveal the flat over the lead.
  var IMG_SKIP = '.mx-card__media img, [fetchpriority="high"], [loading="eager"], .pop-nowait, header img, .ma-hero img, .ma-hero__media img, .maws-hero__pic img, .maws-brandmark';
  var imgIO = null;
  function armImg(img, top) {
    if (img.complete || img.__popArmed || top < (window.innerHeight || 800) * 0.95) return;
    img.__popArmed = 1;
    var wrap = img.parentElement; if (!wrap) return;
    wrap.classList.add('pop-skel'); img.classList.add('pop-wait');
    var done = false;
    function ok() { if (done) return; done = true; img.classList.add('pop-ld'); wrap.classList.remove('pop-skel'); setTimeout(function () { img.classList.remove('pop-wait', 'pop-ld'); }, 900); }
    img.addEventListener('load', ok, { once: true }); img.addEventListener('error', ok, { once: true });
    setTimeout(ok, 5000); /* never leave a picture hidden */
  }
  function scanImgs(ctx) {
    if (!('IntersectionObserver' in window)) return;
    if (!imgIO) imgIO = new IntersectionObserver(function (es) {
      es.forEach(function (en) { if (en.isIntersecting) { imgIO.unobserve(en.target); armImg(en.target, en.boundingClientRect.top); } });
    }, { rootMargin: '3000px 0px 3000px 0px' });
    var list = ctx && ctx.nodeType === 1 ? [].slice.call(ctx.querySelectorAll('img[loading="lazy"]')) : [].slice.call(document.querySelectorAll('img[loading="lazy"]'));
    if (ctx && ctx.nodeType === 1 && ctx.matches && ctx.matches('img[loading="lazy"]')) list.push(ctx);
    list.forEach(function (img) { if (!img.__popSeen && !img.complete && !img.matches(IMG_SKIP)) { img.__popSeen = 1; imgIO.observe(img); } });
  }

  /* ---------- 3. reveal ---------- */
  var io = null;
  function armReveal(ctx) {
    if (!('IntersectionObserver' in window)) return;
    if (!io) io = new IntersectionObserver(function (es) {
      es.forEach(function (en) { if (en.isIntersecting) { en.target.classList.add('pop-in'); io.unobserve(en.target); setTimeout(function () { en.target.style.removeProperty('--pop-i'); }, 1600); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    var seen = new Map();
    [].slice.call((ctx || document).querySelectorAll('[data-pop-reveal]:not(.pop-in)')).forEach(function (el) {
      if (el.__popArmed) return; el.__popArmed = 1;
      var r = el.getBoundingClientRect();
      if (r.top < (window.innerHeight || 800) * 0.92) { el.classList.add('pop-in'); el.style.transition = 'none'; return; } /* above the fold: never hidden, never animated */
      var p = el.parentElement, i = seen.get(p) || 0; seen.set(p, i + 1);
      el.style.setProperty('--pop-i', Math.min(i, 5)); io.observe(el);
    });
  }

  /* ---------- 4. rails ---------- */
  var RAILS = 'ul.ma-st__track, ul.ma-x__track, ul.ma-lk__track, ul.ma-hq2-sets__grid, ul.ma-hc__audiences, .ma-st__tabs, .ma-hero-chips, .ma-value-strip, [data-pop-rail]';
  var BARS = '.ma-hero-chips, .ma-value-strip, .ma-st__tabs';
  var CARD_RAILS = 'ul.ma-st__track, ul.ma-x__track, ul.ma-lk__track, ul.ma-hq2-sets__grid';
  function edge(r) {
    var max = r.scrollWidth - r.clientWidth;
    /* a scrollable region with nothing focusable inside must itself be focusable, or a keyboard user cannot scroll it (WCAG 2.1.1) */
    if (max >= 6 && r.__popTab === undefined) { r.__popTab = 1; if (!r.querySelector('a[href], button, input, select, textarea, summary, [tabindex]')) r.tabIndex = 0; }
    if (max < 6) return r.removeAttribute('data-pop-edge');
    var x = Math.abs(r.scrollLeft);
    r.setAttribute('data-pop-edge', x < 6 ? 'start' : x > max - 6 ? 'end' : 'mid');
  }
  function armRail(r) {
    if (r.__popRail) return; r.__popRail = 1;
    if (!r.hasAttribute('data-pop-rail')) r.setAttribute('data-pop-rail', r.matches(BARS) ? 'bar' : 'cards');
    /* storefront product rails get a progress line on phones (css hides it above 750px), unless the section already draws its own */
    if (r.matches(CARD_RAILS) && !r.hasAttribute('data-pop-progress') && !(r.parentNode && r.parentNode.querySelector('[class*="prog"]'))) r.setAttribute('data-pop-progress', '');
    var bar = null, fill = null, tick = false;
    if (r.hasAttribute('data-pop-progress') && r.parentNode) {
      bar = document.createElement('span'); bar.className = 'pop-prog'; bar.setAttribute('aria-hidden', 'true'); fill = document.createElement('i'); bar.appendChild(fill);
      r.parentNode.insertBefore(bar, r.nextSibling);
    }
    function upd() {
      tick = false; edge(r);
      if (fill) { var max = r.scrollWidth - r.clientWidth; fill.style.transform = 'scaleX(' + (max > 0 ? Math.max(0.12, Math.min(1, (Math.abs(r.scrollLeft) / max) * 0.88 + 0.12)).toFixed(3) : 1) + ')'; }
    }
    r.addEventListener('scroll', function () { if (!tick) { tick = true; requestAnimationFrame(upd); } }, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(upd).observe(r); else window.addEventListener('resize', upd);
    upd();
  }
  function scanRails(ctx) { var c = ctx || document; [].slice.call(c.querySelectorAll(RAILS)).forEach(armRail); if (c.matches && c.matches(RAILS)) armRail(c); }

  /* ---------- 5. sheet ---------- */
  /* MaPop.sheet(panel, { grab, handle, onClose, onProgress }) lets a drawer behave as a bottom sheet below 750px:
     grab the handle or top bar (opt.grab is a selector inside the panel), it follows the finger 1:1 (offset from where you grabbed), a flick is projected
     forward (Apple's decay: v / 1000 * d / (1 - d), d = 0.998) and decides close vs. snap back; the release hands the
     finger's velocity to the spring. Pulling past the top rubber-bands. Returns { destroy }. */
  function project(v, d) { d = d || 0.998; return (v / 1000) * d / (1 - d); }
  function rubber(over, dim, c) { c = c || 0.55; return (over * dim * c) / (dim + c * Math.abs(over)); }
  function sheet(panel, opt) {
    opt = opt || {};
    var phone = window.matchMedia('(max-width: 749px)');
    var grip = opt.handle || panel;
    var y = 0, startY = 0, startOff = 0, pts = [], drag = false, anim = null;
    function setY(v) { y = v; panel.style.translate = '0 ' + v + 'px'; if (opt.onProgress) opt.onProgress(Math.min(1, Math.max(0, v / panel.offsetHeight))); }
    function settle(to, vel, close) {
      if (anim) anim.cancel();
      if (calm()) { panel.style.translate = ''; y = 0; if (close) opt.onClose && opt.onClose(); return; }
      var from = y;
      anim = panel.animate({ translate: ['0 ' + from + 'px', '0 ' + to + 'px'] }, { duration: close ? 320 : 520, easing: close ? 'cubic-bezier(0.4, 0, 1, 1)' : (Math.abs(vel) > 600 ? getComputedStyle(root).getPropertyValue('--pop-bounce').trim() || SPRING : SPRING), fill: 'forwards' });
      anim.onfinish = function () { if (close) { opt.onClose && opt.onClose(); } panel.style.translate = ''; y = 0; try { anim.cancel(); } catch (e) { /* done */ } anim = null; };
    }
    function down(e) {
      if (!phone.matches || (e.pointerType === 'mouse' && e.button !== 0)) return;
      if (opt.grab && !e.target.closest(opt.grab)) return;
      if (e.target.closest('input, select, textarea, a, button')) return;
      if (anim) { var cur = parseFloat((getComputedStyle(panel).translate || '0 0').split(' ')[1]) || 0; anim.cancel(); anim = null; setY(cur); } /* grab it mid-flight: continue from the on-screen value, never the target */
      drag = true; startY = e.clientY; startOff = y; pts = [{ t: e.timeStamp, y: e.clientY }];
      try { grip.setPointerCapture(e.pointerId); } catch (x) { /* ok */ }
    }
    function move(e) {
      if (!drag) return;
      var dy = e.clientY - startY + startOff;
      pts.push({ t: e.timeStamp, y: e.clientY }); if (pts.length > 6) pts.shift();
      setY(dy < 0 ? -rubber(-dy, panel.offsetHeight) : dy);
    }
    function up(e) {
      if (!drag) return; drag = false;
      var a = pts[0], b = pts[pts.length - 1], v = b.t > a.t ? ((b.y - a.y) / (b.t - a.t)) * 1000 : 0; /* px per second */
      var end = y + project(v);
      var h = panel.offsetHeight;
      if (end > h * 0.45 || v > 900) settle(h, v, true); else settle(0, v, false);
      try { grip.releasePointerCapture(e.pointerId); } catch (x) { /* ok */ }
    }
    grip.addEventListener('pointerdown', down); grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up); grip.addEventListener('pointercancel', up);
    return { destroy: function () { grip.removeEventListener('pointerdown', down); grip.removeEventListener('pointermove', move); grip.removeEventListener('pointerup', up); grip.removeEventListener('pointercancel', up); } };
  }

  /* ---------- 6. sticky call to action ---------- */
  /* [data-pop-stick data-after=".hero" data-until="#apply"]: a bar that slides up once the first block has scrolled away and tucks back
     down when the target block is on screen (so it never sits on top of the thing it points to). Transform only. */
  function armStick(el) {
    if (el.__popStick || !('IntersectionObserver' in window)) return; el.__popStick = 1;
    var a = document.querySelector(el.getAttribute('data-after')), u = el.getAttribute('data-until') ? document.querySelector(el.getAttribute('data-until')) : null;
    if (!a) return;
    var pastA = false, onU = false;
    function sync() {
      var on = pastA && !onU;
      if (on && el.hidden) { el.hidden = false; void el.offsetWidth; }
      el.classList.toggle('is-on', on); el.setAttribute('aria-hidden', on ? 'false' : 'true');
      el.querySelectorAll('a,button').forEach(function (n) { n.tabIndex = on ? 0 : -1; });
    }
    new IntersectionObserver(function (es) { pastA = !es[0].isIntersecting && es[0].boundingClientRect.top < 0; sync(); }).observe(a);
    if (u) new IntersectionObserver(function (es) { onU = es[0].isIntersecting; sync(); }, { rootMargin: '0px 0px -10% 0px' }).observe(u);
  }
  function scanStick(ctx) { [].slice.call((ctx || document).querySelectorAll('[data-pop-stick]')).forEach(armStick); }

  /* ---------- 7. storefront quick-add sheet ---------- */
  /* The storefront already opens a bottom sheet for the "+" on a card (.ma-qs-sheet). It gets the same grab handle and the same drag to
     dismiss as the portal sheets; closing goes through the sheet's own close button, so its state stays in step. */
  function armQuickSheet(panel) {
    if (panel.__popSheet) return; panel.__popSheet = 1;
    var grip = document.createElement('div'); grip.className = 'pop-grip'; grip.setAttribute('aria-hidden', 'true');
    panel.insertBefore(grip, panel.firstChild);
    sheet(panel, { grab: '.pop-grip', onClose: function () { var c = panel.querySelector('button[class*="close"], .ma-x__btn, button[aria-label*="lose" i]'); if (c) c.click(); } });
  }
  function scanQuick(ctx) { var c = ctx || document; [].slice.call(c.querySelectorAll('.ma-qs-sheet__panel')).forEach(armQuickSheet); }

  /* ---------- boot ---------- */
  function scan(ctx) { scanImgs(ctx); armReveal(ctx); scanRails(ctx); scanStick(ctx); scanQuick(ctx); }
  window.MaPop = { sheet: sheet, project: project, rubber: rubber, scan: scan, spring: SPRING };
  function start() {
    scan();
    var queue = [], t = null;
    new MutationObserver(function (ms) {
      ms.forEach(function (m) { m.addedNodes.forEach(function (n) { if (n.nodeType === 1 && !(n.classList && (n.classList.contains('pop-prog')))) queue.push(n); }); });
      if (!queue.length || t) return;
      t = setTimeout(function () { var q = queue; queue = []; t = null; q.slice(0, 40).forEach(function (n) { if (n.isConnected) scan(n); }); }, 120); /* only the added subtrees, never the whole document */
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
}());
